import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TakeManager = ({
  takes,
  channelKey,
  onSelectTake,
  onDeleteTake,
  onRenameTake,
  onUpdateTakeClip,
  onDuplicateTake,
  onPromoteTakeToComp,
  onToggleTakeTimelineActive,
  onSplitTake,
  onResetTakeClip,
  onBeginEdit,
  disabled
}) => {
  const [editingTake, setEditingTake] = useState(null);
  const [nameInput, setNameInput] = useState('');

  if (!takes || takes?.length === 0) return null;

  const formatDuration = (duration) => {
    if (typeof duration !== 'number') return duration || '--';
    return `${Math.floor(duration / 60)}:${Math.floor(duration % 60)?.toString()?.padStart(2, '0')}`;
  };
  const formatSeconds = (value) => {
    const numeric = Number(value) || 0;
    return numeric.toFixed(2);
  };
  const updateClip = (take, updates) => {
    onUpdateTakeClip?.(channelKey, take?.id, updates);
  };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Icon name="Layers" size={13} color="#f59e0b" />
          <span className="text-xs font-mono text-gray-300 uppercase tracking-wider">Takes — {channelKey}</span>
        </div>
        <span className="text-xs font-mono text-gray-500">{takes?.length} take{takes?.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="divide-y divide-gray-700/50 max-h-48 overflow-y-auto">
        {takes?.map((take, idx) => {
          const clipStart = Number(take?.clipStart ?? 0) || 0;
          const clipEnd = Number(take?.clipEnd ?? take?.duration ?? 0) || 0;
          const clipOffset = Number(take?.clipOffset ?? 0) || 0;
          const maxDuration = Math.max(0, Number(take?.duration) || 0);

          return (
          <div
            key={take?.id}
            className={`px-3 py-2 transition-all cursor-pointer ${
              take?.isSelected
                ? 'bg-amber-900/20 border-l-2 border-amber-500' :'hover:bg-gray-800/50 border-l-2 border-transparent'
            }`}
            onClick={() => !disabled && onSelectTake(channelKey, take?.id)}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                take?.isSelected ? 'bg-amber-400' : 'bg-gray-600'
              }`} />
              {editingTake === take?.id ? (
                <input
                  autoFocus
                  value={nameInput}
                  onChange={e => setNameInput(e?.target?.value)}
                  onBlur={() => { onRenameTake(channelKey, take?.id, nameInput); setEditingTake(null); }}
                  onKeyDown={e => {
                    if (e?.key === 'Enter') { onRenameTake(channelKey, take?.id, nameInput); setEditingTake(null); }
                    if (e?.key === 'Escape') setEditingTake(null);
                  }}
                  onClick={e => e?.stopPropagation()}
                  className="flex-1 text-xs font-mono bg-gray-700 border border-gray-500 rounded px-1 py-0.5 text-white"
                  maxLength={20}
                />
              ) : (
                <span className={`flex-1 text-xs font-mono ${
                  take?.isSelected ? 'text-amber-300' : 'text-gray-400'
                }`}>
                  {take?.name || `Take ${idx + 1}`}
                </span>
              )}
              <span className="text-[10px] font-mono text-gray-600">{formatDuration(take?.duration)}</span>
              <span
                className={`rounded border px-1.5 py-0.5 text-[8px] font-mono uppercase ${
                  take?.timelineActive && take?.isSelected
                    ? 'border-amber-500/50 bg-amber-500/10 text-amber-300'
                    : take?.timelineActive
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                    : 'border-gray-700 bg-gray-950 text-gray-600'
                }`}
                title={take?.timelineActive ? 'Active on timeline' : 'Inactive alternate take'}
              >
                {take?.timelineActive && take?.isSelected ? 'Comp' : take?.timelineActive ? 'Timeline' : 'Alt'}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={e => { e?.stopPropagation(); onToggleTakeTimelineActive?.(channelKey, take?.id); }}
                  disabled={disabled}
                  className={`w-5 h-5 flex items-center justify-center rounded ${
                    take?.timelineActive
                      ? 'hover:bg-red-900/30 text-emerald-400 hover:text-red-300'
                      : 'hover:bg-emerald-900/30 text-gray-500 hover:text-emerald-300'
                  }`}
                  title={take?.timelineActive ? 'Remove from timeline' : 'Add to timeline'}
                >
                  <Icon name={take?.timelineActive ? 'MinusCircle' : 'PlusCircle'} size={10} />
                </button>
                <button
                  onClick={e => { e?.stopPropagation(); setEditingTake(take?.id); setNameInput(take?.name || `Take ${idx + 1}`); }}
                  disabled={disabled}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-700 text-gray-500 hover:text-gray-300"
                  title="Rename take"
                >
                  <Icon name="Pencil" size={10} />
                </button>
                <button
                  onClick={e => { e?.stopPropagation(); onDuplicateTake?.(channelKey, take?.id); }}
                  disabled={disabled}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-blue-900/30 text-gray-500 hover:text-blue-400"
                  title="Duplicate take"
                >
                  <Icon name="Copy" size={10} />
                </button>
                <button
                  onClick={e => { e?.stopPropagation(); onPromoteTakeToComp?.(channelKey, take?.id); }}
                  disabled={disabled}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-amber-900/30 text-gray-500 hover:text-amber-300"
                  title="Use as comp"
                >
                  <Icon name="CheckCircle2" size={10} />
                </button>
                <button
                  onClick={e => { e?.stopPropagation(); onDeleteTake(channelKey, take?.id); }}
                  disabled={disabled}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-900/30 text-gray-500 hover:text-red-400"
                  title="Delete take"
                >
                  <Icon name="Trash2" size={10} />
                </button>
              </div>
            </div>

            {take?.isSelected && (
              <div onClick={(e) => e?.stopPropagation()}>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <label className="space-y-1">
                    <span className="block text-[9px] font-mono text-gray-500 uppercase">Start</span>
                    <input
                      type="number"
                      min="0"
                      max={formatSeconds(maxDuration)}
                      step="0.05"
                      value={formatSeconds(clipStart)}
                      onFocus={() => onBeginEdit?.()}
                      onChange={(e) => updateClip(take, { clipStart: e?.target?.value })}
                      disabled={disabled}
                      className="w-full rounded border border-gray-700 bg-gray-950 px-2 py-1 text-[10px] font-mono text-gray-200 disabled:opacity-40"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-[9px] font-mono text-gray-500 uppercase">End</span>
                    <input
                      type="number"
                      min={formatSeconds(clipStart)}
                      max={formatSeconds(maxDuration)}
                      step="0.05"
                      value={formatSeconds(clipEnd)}
                      onFocus={() => onBeginEdit?.()}
                      onChange={(e) => updateClip(take, { clipEnd: e?.target?.value })}
                      disabled={disabled}
                      className="w-full rounded border border-gray-700 bg-gray-950 px-2 py-1 text-[10px] font-mono text-gray-200 disabled:opacity-40"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-[9px] font-mono text-gray-500 uppercase">Offset</span>
                    <input
                      type="number"
                      min="0"
                      step="0.05"
                      value={formatSeconds(clipOffset)}
                      onFocus={() => onBeginEdit?.()}
                      onChange={(e) => updateClip(take, { clipOffset: e?.target?.value })}
                      disabled={disabled}
                      className="w-full rounded border border-gray-700 bg-gray-950 px-2 py-1 text-[10px] font-mono text-gray-200 disabled:opacity-40"
                    />
                  </label>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onPromoteTakeToComp?.(channelKey, take?.id)}
                    disabled={disabled}
                    className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-mono uppercase text-amber-300 hover:border-amber-400 hover:bg-amber-500/15 disabled:opacity-40"
                  >
                    Use as Comp
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleTakeTimelineActive?.(channelKey, take?.id)}
                    disabled={disabled}
                    className={`rounded border px-2 py-1 text-[10px] font-mono uppercase disabled:opacity-40 ${
                      take?.timelineActive
                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:border-red-500/60 hover:text-red-300'
                        : 'border-gray-700 text-gray-400 hover:border-emerald-500/60 hover:text-emerald-300'
                    }`}
                  >
                    {take?.timelineActive ? 'Remove from Timeline' : 'Add to Timeline'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSplitTake?.(channelKey, take?.id)}
                    disabled={disabled}
                    className="rounded border border-gray-700 px-2 py-1 text-[10px] font-mono uppercase text-gray-400 hover:border-emerald-500/60 hover:text-emerald-300 disabled:opacity-40"
                  >
                    Split
                  </button>
                  <button
                    type="button"
                    onClick={() => onResetTakeClip?.(channelKey, take?.id)}
                    disabled={disabled}
                    className="rounded border border-gray-700 px-2 py-1 text-[10px] font-mono uppercase text-gray-400 hover:border-amber-500/60 hover:text-amber-300 disabled:opacity-40"
                  >
                    Reset Clip
                  </button>
                  <button
                    type="button"
                    onClick={() => onDuplicateTake?.(channelKey, take?.id)}
                    disabled={disabled}
                    className="rounded border border-gray-700 px-2 py-1 text-[10px] font-mono uppercase text-gray-400 hover:border-blue-500/60 hover:text-blue-300 disabled:opacity-40"
                  >
                    Duplicate
                  </button>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default TakeManager;
